import moment from 'moment';
import { maxBy } from 'lodash';

import { PERFORMANCE_CHART, TIME_RANGE_PRESET, PERIODS_VALUE_CI, PERIODS_VALUE_INC, TIME_PERIODS, COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { getDateQuickRangeFromPreset, calculateDateRangeFromPeriod } from '@/utils/time-range-presets';
import { getMaxEndDate } from '@/utils/dates';
import { getEarliestCommonInceptionDate } from '@/utils/comparison/items';
import { periodToMonths } from '@/utils/time';
import User from '@/types/user';
import Comparison from '@/types/comparison';
import { getGraphPerformanceDisclaimer } from '@/utils/disclaimers/charts';
import { DEFAULT_OPTIONS } from '@/views/components/charts/deprecated/charts/GrowthChart';
import { getRollingIntervalFromDates, monthsToPeriodFromValues } from '@/utils/charts';
import ComparisonItem from '@/types/comparison-item';
import { DrawdownChartState, GrowthChartState, RiskRewardChartState } from '../types';

export function getAvailableCharts(user: User, comparison: Comparison) {
  const options = [
    {
      chart: PERFORMANCE_CHART.GROWTH,
    },
    {
      chart: PERFORMANCE_CHART.RISK_REWARD,
      permission: PERMISSIONS.RISK_REWARD_ANALYSIS,
    },
    {
      chart: PERFORMANCE_CHART.DRAWDOWN,
      permission: PERMISSIONS.DRAWDOWN_ANALYSIS,
    },
  ];

  return options
    .filter(option => !option.permission || !!comparison.providedBy || userHasPermissionEnabled(user, option.permission))
    .map(option => option.chart);
}

export function handleQuickDateChange(
  dateQuickRange: any,
  comparison: Comparison,
  monthsRange: number,
  items: ComparisonItem[],
  useMonthly: boolean,
): Partial<GrowthChartState> | Partial<DrawdownChartState> | Partial<RiskRewardChartState> {
  const endDate = getMaxEndDate(comparison.monthEndDataOption);

  // Use the unified helper function with chart-specific options
  const result = calculateDateRangeFromPeriod(dateQuickRange, {
    referenceEndDate: endDate,
    items,
    useMonthly,
    getEarliestCommonInceptionDate,
    applyRelevanceYearLimit: false,
  });

  let { startDate } = result;
  let { months } = result;

  if (!startDate && (months === PERIODS_VALUE_CI || months === PERIODS_VALUE_INC)) {
    months = monthsRange;
    if (months > 0) {
      startDate = moment(endDate).startOf('month').subtract(months, 'months').toDate();
    }
  }

  if (!months) {
    months = periodToMonths(dateQuickRange) || monthsRange;
  }

  return {
    dateQuickRange,
    startDate,
    endDate,
    isYTD: result.isYTD ?? (months !== PERIODS_VALUE_CI && months !== PERIODS_VALUE_INC && months < 12),
    monthsRange: months,
  };
}

export function getChartWarningForType({
  chartType,
  items,
  interval,
  startDate,
  endDate,
  useMonthly,
}: {
  chartType: PERFORMANCE_CHART;
  items: any[];
  interval: any;
  startDate: Date | null;
  endDate: Date;
  useMonthly?: boolean;
}) {
  if (chartType === PERFORMANCE_CHART.RISK_REWARD) return null;
  return getGraphPerformanceDisclaimer(items, interval, startDate, endDate, undefined, useMonthly) || null;
}

function getFormattedDateQuickRange(user: User, startDate: Date | null | undefined, endDate: Date | null | undefined, comparison: Comparison) {
  const dateQuickRangeUserPreference = user.preferences?.comparisonSettings?.hypotheticalReturnChartHorizon;
  const dateQuickRangeValues = getDateQuickRangeFromPreset(TIME_RANGE_PRESET.MULTI_ITEMS_DEFAULT, comparison.shortHistory);
  const dateRange = startDate ? moment(endDate).diff(startDate, 'months') : null;
  let result = monthsToPeriodFromValues(dateRange, undefined, dateQuickRangeValues) || dateQuickRangeUserPreference;

  // Use the last value in dateQuickRangeValues to determine CI/INC since shortHistory can sometimes be undefined
  // This happens because inside monthsToPeriod method, we default to INC when a case doesn't match. and we never accounted for CI
  const lastQuickRange = dateQuickRangeValues[dateQuickRangeValues.length - 1];
  if (result === TIME_PERIODS.INC && lastQuickRange === TIME_PERIODS.CI) {
    result = TIME_PERIODS.CI;
  } else if (result === TIME_PERIODS.CI && lastQuickRange === TIME_PERIODS.INC) {
    result = TIME_PERIODS.INC;
  }

  return result;
}

export function getGrowthChartState(user: User, comparison: Comparison): GrowthChartState {
  const options = comparison?.options?.growth || {};
  const formattedDateQuickRange = getFormattedDateQuickRange(user, options.startDate, options.endDate, comparison);

  return {
    capital: options.capital ?? DEFAULT_OPTIONS.capital,
    targetROI: options.targetROI,
    dateQuickRange: formattedDateQuickRange,
    startDate: options.startDate ?? DEFAULT_OPTIONS.startDate,
    endDate: options.endDate ?? DEFAULT_OPTIONS.endDate,
    useMonthly: options.useMonthly ?? false,
    rebalancePeriod: options.rebalancePeriod,
  };
}

export function getDrawdownChartState(user: User, comparison: Comparison): DrawdownChartState {
  const options = comparison?.options?.drawdown || {};
  const growthState = getGrowthChartState(user, comparison);
  const formattedDateQuickRange = getFormattedDateQuickRange(user, options.startDate, options.endDate, comparison);

  return {
    startDate: options.startDate ?? growthState.startDate,
    endDate: options.endDate ?? growthState.endDate,
    dateQuickRange: formattedDateQuickRange,
    useMonthly: options.useMonthly ?? false,
  };
}

export function getRiskRewardChartState(user: User, comparison: Comparison): RiskRewardChartState {
  // user will be used when we reintroduce benchmark
  const options = comparison?.options?.riskReward || {};

  return {
    monthsRange: options.monthsRange ?? 36,
    dateQuickRange: options.dateQuickRange ?? TIME_PERIODS['3Y'],
  };
}

export function getChartWarning(selectedChart: PERFORMANCE_CHART, comparison: Comparison, items: ComparisonItem[], user: User) {
  const growthChart = getGrowthChartState(user, comparison);
  const drawdownChart = getDrawdownChartState(user, comparison);

  let startDate: Date | null = null;
  let { endDate } = DEFAULT_OPTIONS;
  let useMonthly: boolean = false;

  switch (selectedChart) {
    case PERFORMANCE_CHART.GROWTH:
      startDate = growthChart.startDate;
      endDate = growthChart.endDate;
      useMonthly = growthChart.useMonthly ?? false;
      break;
    case PERFORMANCE_CHART.DRAWDOWN:
      startDate = drawdownChart.startDate;
      endDate = drawdownChart.endDate;
      useMonthly = drawdownChart.useMonthly ?? false;
      break;
    // We currently are not displaying warning for ChartWarning at all to begin with due to the removal of the Benchmark
  }

  const interval = getRollingIntervalFromDates(startDate, endDate, items, useMonthly, comparison.shortHistory);

  return getChartWarningForType({
    chartType: selectedChart,
    items,
    interval,
    startDate,
    endDate,
    useMonthly,
  });
}
